import { useCallback, useEffect, useState } from "react";
import {
  listOriginations,
  type RecipientHint,
} from "../api/deferred-originations";
import { useSessionStore } from "../store/useSessionStore";
import { toTitleCase } from "../utils/titleCase";

export interface RecentRecipient {
  /** Stable key for list rendering (normalized name). */
  key: string;
  /** Display / form value (Title Case). */
  fullName: string;
  timesUsed: number;
  lastUsedAt: string;
}

export interface UseRecentRecipientsState {
  recipients: RecentRecipient[];
  loading: boolean;
  refresh: () => void;
}

const hintLabel = (hint: RecipientHint): string => {
  const fromParts = [hint.firstName, hint.lastName]
    .filter((p) => Boolean(p?.trim()))
    .join(" ")
    .trim();
  const raw = hint.fullName?.trim() || fromParts;
  return toTitleCase(raw);
};

const normalizeKey = (name: string): string =>
  name.trim().toLowerCase().replace(/\s+/g, " ");

/**
 * Builds a "recent recipients" list from the sender's deferred originations —
 * no backend recipient-book endpoint required.
 */
export const useRecentRecipients = (
  linkIdOverride?: string | null,
  limit = 10
): UseRecentRecipientsState => {
  const sessionLinkId = useSessionStore((s) => s.session.data?.linkId ?? null);
  const linkId = linkIdOverride || sessionLinkId;

  const [recipients, setRecipients] = useState<RecentRecipient[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    if (!linkId) {
      setRecipients([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    listOriginations(linkId)
      .then((items) => {
        const byKey = new Map<string, RecentRecipient>();
        for (const item of items) {
          const fullName = hintLabel(item.recipientHint);
          if (!fullName || fullName.split(/\s+/).length < 2) continue;
          const key = normalizeKey(fullName);
          const prev = byKey.get(key);
          if (!prev) {
            byKey.set(key, {
              key,
              fullName,
              timesUsed: 1,
              lastUsedAt: item.createdAt,
            });
          } else {
            prev.timesUsed += 1;
            if (item.createdAt > prev.lastUsedAt) {
              prev.lastUsedAt = item.createdAt;
              prev.fullName = fullName;
            }
          }
        }
        const sorted = [...byKey.values()].sort((a, b) =>
          b.lastUsedAt.localeCompare(a.lastUsedAt)
        );
        setRecipients(sorted.slice(0, limit));
      })
      .catch(() => setRecipients([]))
      .finally(() => setLoading(false));
  }, [linkId, limit]);

  useEffect(() => {
    load();
  }, [load]);

  return { recipients, loading, refresh: load };
};
